import { callApi } from '@/api/callApi';
import {
  API_ENDPOINT,
  API_URL_CMS_SERVICE,
  API_URL_PRODUCT_SERVICE,
} from '@/api/constant';
import {
  IMediaProp,
  IMediaResponse,
} from '@/interfaces/mediaResponse.interface';
import React, { useEffect, useState } from 'react';
import MediaInputField from '../common/media/MediaInputField';
import MediaPreview from '../common/media/MediaPreview';
import { showToastNotification } from '@/utility/helper';

interface UploadFieldProps {
  label?: string;
  helperText?: string;
  multiple?: boolean;
  referenceType?: string;
  referenceId?: string;
  initialMedia?: IMediaProp[];
  accept?: string;
  onChangeMedia?: (media: IMediaProp[]) => void;
  apiType?: 'product' | 'content';
  folderUpload?: boolean;
  showPreviews?: boolean;
}

const UploadField: React.FC<UploadFieldProps> = ({
  label,
  helperText = 'Select atleast one media file',
  referenceType,
  referenceId,
  initialMedia,
  multiple = true,
  accept = '*/*',
  apiType = 'product',
  onChangeMedia,
  folderUpload = false,
  showPreviews = true,
}) => {
  const [mediaUrls, setMediaUrls] = useState<IMediaProp[]>(initialMedia || []);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasUserChanged, setHasUserChanged] = useState(false);
  const [progress, setProgress] = useState(0);

  const getMediaUploadUrl = () => {
    const urls: Record<string, string> = {
      product: API_URL_PRODUCT_SERVICE,
      content: API_URL_CMS_SERVICE,
    };
    return urls[apiType];
  };

  useEffect(() => {
    // Only update from parent if user hasn't modified local state yet
    if (!hasUserChanged && initialMedia && initialMedia.length > 0) {
      setMediaUrls(
        initialMedia.map((m) => ({
          ...m,
          url: m.url.startsWith(getMediaUploadUrl())
            ? m.url
            : `${getMediaUploadUrl()}/${m.url}`,
        })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMedia]);

  const getMediaType = (file: File): 'image' | 'video' =>
    file.type.startsWith('video/') ? 'video' : 'image';

  const handleMediaChange = (updatedMedia: IMediaProp[]) => {
    setHasUserChanged(true);
    setMediaUrls(updatedMedia);
    onChangeMedia?.(updatedMedia);
  };

  const handleUploadFile = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const uploaded: IMediaProp[] = [];
    const PATH_BASE = `${getMediaUploadUrl()}${API_ENDPOINT.media}`;
    const isMultiple = multiple && files.length > 1;
    const PATH = isMultiple ? `${PATH_BASE}/multiple` : PATH_BASE;

    const createFormData = () => {
      const formData = new FormData();
      if (referenceType) formData.append('referenceType', referenceType);
      if (!isMultiple && referenceId)
        formData.append('referenceId', referenceId);
      return formData;
    };

    const handleApiCall = async (formData: FormData) => {
      setIsLoading(true);
      try {
        const res = await callApi<IMediaResponse>(PATH, {
          method: 'POST',
          multipart: true,
          data: formData,
          onUploadProgress: (progress) => setProgress(progress),
        });
        setIsLoading(false);
        return res;
      } catch (error: unknown) {
        setIsLoading(false);
        const err = error as { message?: string };
        showToastNotification('danger', `Upload failed: ${err.message}`);
        // console.error('Upload failed:', error);
      }
    };

    if (isMultiple) {
      const formData = createFormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }

      const res = await handleApiCall(formData);
      if (res) {
        uploaded.push({
          type: 'image',
          id: res.id,
          url: `${getMediaUploadUrl()}/${res.url}`,
          altText: res.altText,
          referenceType,
        });
      }
    } else {
      const file = files[0];
      const type = getMediaType(file);
      const altText = file.name;

      const formData = createFormData();
      formData.append('file', file);
      formData.append('type', type);
      formData.append('altText', altText);

      const res = await handleApiCall(formData);
      if (res) {
        uploaded.push({
          type,
          id: res.id,
          url: `${getMediaUploadUrl()}/${res.url}`,
          altText,
          referenceType,
          referenceId,
        });
        showToastNotification('success', 'Image uploaded on server');
      }
    }

    if (onChangeMedia) onChangeMedia(uploaded);
    handleMediaChange([...mediaUrls, ...uploaded]);
  };

  const handleDeleteMedia = async (id?: string, url?: string) => {
    try {
      if (id === undefined && url === undefined) return;

      let PATH = `${getMediaUploadUrl()}${API_ENDPOINT.media}`;
      let data: { path?: string; id?: string } | null = null;
      if (id) {
        data = { id: id };
      } else if (url) {
        const cleanedUrl = url && url.replace(/^.*\/uploads\//, 'uploads/');
        data = {
          path: cleanedUrl,
        };
      }

      const updated = id
        ? mediaUrls.filter((m) => m.id !== id)
        : mediaUrls.filter((m) => m.url !== url);
      handleMediaChange(updated);

      await callApi(PATH, { method: 'DELETE', data });
    } catch (error) {
      showToastNotification(
        'info',
        'Failed to delete the media from the server. It has been removed from the UI.',
      );
      console.error('Error deleting media:', error);
    }
  };

  return (
    <>
      <MediaInputField
        multiple={multiple}
        onFilesChange={handleUploadFile}
        helperText={helperText}
        folderUpload={folderUpload}
        label={label}
        progress={progress}
        isLoading={isLoading}
        accept={accept}
        disable={!multiple && mediaUrls.length > 0}
      />

      {showPreviews && (
        <div
          className={`grid ${multiple ? 'grid-cols-2' : 'grid-cols-1'} gap-4 mt-4`}
        >
          <MediaPreview
            mediaUrls={mediaUrls}
            handleDeleteMedia={handleDeleteMedia}
          />
        </div>
      )}
    </>
  );
};

export default UploadField;
